// Reusable exercise-picker modal. Used by:
//  - the Routine editor when adding exercises to a routine
//  - the active Workout session when adding exercises mid-session
//
// The legacy AngularJS app had two near-identical pickers; we collapse them
// into one component here.
import type { Exercise, SubMuscleGroup } from '@onflow/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useExercises } from '../hooks/useExercises.js';

// Locked taxonomy. Two levels:
//   - group: the primary chip ("Legs", "Chest", ...)
//   - subChip: the secondary chip ("Quads", "Mid Chest", ...). Each subChip
//     bundles one or more SubMuscleGroup enum values so a chip like "Quads"
//     can pick up both `quads` and `vastus_medialis`-tagged exercises.
// Filter happens by sub-muscle membership, not by Exercise.primaryMuscle —
// this keeps the chip set deterministic even when an exercise's primary
// muscle and sub-muscle tag don't line up (e.g. Dumbbell Pullover is
// chest-primary but lats-tagged — it surfaces under Back, not Chest).
interface SubChip {
  key: string;
  label: string;
  members: readonly SubMuscleGroup[];
}

interface MuscleGroupEntry {
  key: string;
  label: string;
  subChips: readonly SubChip[];
}

const MUSCLE_GROUPS: readonly MuscleGroupEntry[] = [
  {
    key: 'chest',
    label: 'Chest',
    subChips: [
      { key: 'upper_chest', label: 'Upper Chest', members: ['upper_chest'] },
      { key: 'mid_chest', label: 'Mid Chest', members: ['mid_chest'] },
      { key: 'lower_chest', label: 'Lower Chest', members: ['lower_chest'] },
    ],
  },
  {
    key: 'back',
    label: 'Back',
    subChips: [
      { key: 'lats', label: 'Lats', members: ['lats'] },
      { key: 'upper_back', label: 'Upper Back', members: ['upper_back'] },
      { key: 'lower_back', label: 'Lower Back', members: ['lower_back'] },
      { key: 'traps', label: 'Traps', members: ['traps'] },
    ],
  },
  {
    key: 'shoulders',
    label: 'Shoulders',
    subChips: [
      { key: 'front_delts', label: 'Front Delts', members: ['front_delts'] },
      { key: 'side_delts', label: 'Side Delts', members: ['side_delts'] },
      { key: 'rear_delts', label: 'Rear Delts', members: ['rear_delts'] },
    ],
  },
  {
    key: 'biceps',
    label: 'Biceps',
    subChips: [
      { key: 'short_head', label: 'Short Head', members: ['short_head'] },
      { key: 'long_head', label: 'Long Head', members: ['long_head'] },
      { key: 'brachialis', label: 'Brachialis', members: ['brachialis'] },
    ],
  },
  {
    key: 'triceps',
    label: 'Triceps',
    subChips: [
      { key: 'long_head_triceps', label: 'Long Head', members: ['long_head_triceps'] },
      { key: 'lateral_head', label: 'Lateral Head', members: ['lateral_head'] },
      { key: 'medial_head', label: 'Medial Head', members: ['medial_head'] },
    ],
  },
  {
    key: 'forearms',
    label: 'Forearms',
    subChips: [{ key: 'forearms', label: 'Forearms', members: ['forearms'] }],
  },
  {
    key: 'legs',
    label: 'Legs',
    subChips: [
      { key: 'quads', label: 'Quads', members: ['quads', 'vastus_medialis'] },
      { key: 'hamstrings', label: 'Hamstrings', members: ['hamstrings'] },
      { key: 'glutes', label: 'Glutes', members: ['glutes'] },
      { key: 'calves', label: 'Calves', members: ['gastrocnemius', 'soleus'] },
      { key: 'adductors', label: 'Adductors', members: ['adductors'] },
    ],
  },
  {
    key: 'core',
    label: 'Core',
    subChips: [
      { key: 'upper_abs', label: 'Upper Abs', members: ['upper_abs'] },
      { key: 'lower_abs', label: 'Lower Abs', members: ['lower_abs'] },
      { key: 'obliques', label: 'Obliques', members: ['obliques'] },
    ],
  },
  {
    key: 'cardio',
    label: 'Cardio',
    subChips: [{ key: 'cardio', label: 'Cardio', members: ['cardio'] }],
  },
] as const;

// Reverse lookup: sub-muscle → matching subChip + group. Built once at
// module load. Used to derive row labels like "Quads · barbell" from an
// exercise's `subMuscleGroup` tag.
const SUBMUSCLE_TO_CHIP: Map<SubMuscleGroup, { group: MuscleGroupEntry; subChip: SubChip }> =
  (() => {
    const m = new Map<SubMuscleGroup, { group: MuscleGroupEntry; subChip: SubChip }>();
    for (const g of MUSCLE_GROUPS) {
      for (const sc of g.subChips) {
        for (const sm of sc.members) m.set(sm, { group: g, subChip: sc });
      }
    }
    return m;
  })();

export interface ExercisePickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (exercise: Exercise) => void;
  // Hide exercises that the caller already added — useful in the workout
  // session where duplicates make no sense.
  excludeIds?: string[];
}

export function ExercisePicker({ open, onClose, onPick, excludeIds = [] }: ExercisePickerProps) {
  const { data, isLoading } = useExercises();
  const [search, setSearch] = useState('');
  const [groupKey, setGroupKey] = useState<string>('');
  const [subChipKey, setSubChipKey] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Reset filters every time the modal opens so reopening from a new context
  // doesn't show stale state.
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setSearch('');
      setGroupKey('');
      setSubChipKey('');
      window.requestAnimationFrame(() => searchRef.current?.focus());
    }
    return () => previouslyFocusedRef.current?.focus();
  }, [open]);

  // Close on Escape — matches user expectation for a modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const exercises = useMemo(() => data ?? [], [data]);
  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);

  const activeGroup = useMemo(() => MUSCLE_GROUPS.find((g) => g.key === groupKey), [groupKey]);
  const activeSubChip = useMemo(
    () => activeGroup?.subChips.find((sc) => sc.key === subChipKey),
    [activeGroup, subChipKey],
  );

  // Build the sub-muscle filter set for the active group: a Set for O(1)
  // lookup in the filter loop below.
  const activeGroupMemberSet = useMemo(() => {
    if (!activeGroup) return null;
    const out = new Set<SubMuscleGroup>();
    for (const sc of activeGroup.subChips) {
      for (const sm of sc.members) out.add(sm);
    }
    return out;
  }, [activeGroup]);

  const activeSubChipMemberSet = useMemo(() => {
    if (!activeSubChip) return null;
    return new Set<SubMuscleGroup>(activeSubChip.members);
  }, [activeSubChip]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return exercises.filter((ex) => {
      if (excluded.has(ex.id)) return false;
      const sm = ex.subMuscleGroup;
      if (activeGroupMemberSet) {
        if (!sm || !activeGroupMemberSet.has(sm)) return false;
      }
      if (activeSubChipMemberSet) {
        if (!sm || !activeSubChipMemberSet.has(sm)) return false;
      }
      if (q && !ex.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [exercises, search, activeGroupMemberSet, activeSubChipMemberSet, excluded]);

  if (!open) return null;

  const showSubChipRow = !!activeGroup && activeGroup.subChips.length > 1;

  return createPortal(
    <div
      className="exercise-picker-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.65)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        className="exercise-picker-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exercise-picker-title"
        style={{
          background: 'var(--surface-container-low)',
          border: '1px solid var(--outline-variant)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: 20,
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flex: '0 0 auto',
          }}
        >
          <h2
            id="exercise-picker-title"
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--on-surface)',
              margin: 0,
            }}
          >
            Add Exercise
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close exercise picker"
            style={{
              background: 'var(--surface-container-high)',
              border: 'none',
              color: 'var(--on-surface-variant)',
              width: 32,
              height: 32,
              borderRadius: '50%',
              fontSize: 20,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <input
            ref={searchRef}
            aria-label="Search exercises"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search exercises…"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--surface-container)',
              border: '1px solid var(--outline-variant)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--on-surface)',
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            flex: '0 0 auto',
          }}
        >
          {[{ key: '', label: 'All' }, ...MUSCLE_GROUPS].map((g) => {
            const isActive = groupKey === g.key;
            return (
              <button
                key={g.key || 'all'}
                type="button"
                onClick={() => {
                  setGroupKey(g.key);
                  setSubChipKey('');
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-full)',
                  border: `1px solid ${isActive ? 'var(--primary)' : 'var(--outline-variant)'}`,
                  background: isActive ? 'rgba(232, 106, 58, 0.12)' : 'transparent',
                  color: isActive ? 'var(--primary)' : 'var(--on-surface-variant)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {g.label}
              </button>
            );
          })}
        </div>

        {showSubChipRow && activeGroup && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              flex: '0 0 auto',
              paddingLeft: 8,
              borderLeft: '2px solid var(--outline-variant)',
            }}
          >
            {[{ key: '', label: 'All' }, ...activeGroup.subChips].map((sc) => {
              const isActive = subChipKey === sc.key;
              return (
                <button
                  key={sc.key || 'all-sub'}
                  type="button"
                  onClick={() => setSubChipKey(sc.key)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px solid ${isActive ? 'var(--primary)' : 'var(--outline-variant)'}`,
                    background: isActive ? 'rgba(232, 106, 58, 0.12)' : 'transparent',
                    color: isActive ? 'var(--primary)' : 'var(--on-surface-variant)',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sc.label}
                </button>
              );
            })}
          </div>
        )}

        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {isLoading && (
            <div className="empty-state">
              <p>Loading exercises…</p>
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="empty-state">
              <p>No exercises match.</p>
            </div>
          )}

          {filtered.map((ex) => {
            const sm = ex.subMuscleGroup;
            const chipLabel = sm ? SUBMUSCLE_TO_CHIP.get(sm)?.subChip.label : null;
            return (
              <button
                key={ex.id}
                type="button"
                onClick={() => onPick(ex)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 4px',
                  border: 0,
                  borderBottom: '1px solid var(--outline-variant)',
                  background: 'transparent',
                  fontFamily: 'var(--font-ui)',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--surface-container-high)',
                    color: 'var(--on-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {ex.name.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on-surface)' }}>
                    {ex.name}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--on-surface-variant)' }}>
                    {chipLabel ? `${chipLabel} · ${ex.equipment}` : ex.equipment}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
